import { FMP_KEY, FMP_BASE } from '../constants';

async function fetchFMP(ticker) {
  const t = ticker.toUpperCase().trim();
  const [quoteRes, metricsRes, incomeRes, cashRes, balanceRes, ptRes] = await Promise.all([
    fetch(`${FMP_BASE}/stable/quote?symbol=${t}&apikey=${FMP_KEY}`),
    fetch(`${FMP_BASE}/stable/key-metrics-ttm?symbol=${t}&apikey=${FMP_KEY}`),
    fetch(`${FMP_BASE}/stable/income-statement?symbol=${t}&limit=4&apikey=${FMP_KEY}`),
    fetch(`${FMP_BASE}/stable/cash-flow-statement?symbol=${t}&limit=2&apikey=${FMP_KEY}`),
    fetch(`${FMP_BASE}/stable/balance-sheet-statement?symbol=${t}&limit=2&apikey=${FMP_KEY}`),
    fetch(`${FMP_BASE}/stable/price-target-consensus?symbol=${t}&apikey=${FMP_KEY}`),
  ]);
  const [quote, metrics, income, cash, balance, ptRaw] = await Promise.all([
    quoteRes.json(), metricsRes.json(), incomeRes.json(), cashRes.json(), balanceRes.json(), ptRes.json(),
  ]);
  const ptArr = Array.isArray(ptRaw) ? ptRaw : (ptRaw && typeof ptRaw === "object" && !ptRaw.error ? [ptRaw] : []);
  const ptData = ptArr[0] || {};
  const q  = Array.isArray(quote)   ? quote[0]   : {};
  const m  = Array.isArray(metrics) ? metrics[0] : {};
  const i0 = Array.isArray(income)  ? income[0]  : {};
  const i1 = Array.isArray(income)  ? income[1]  : {};
  const i3 = Array.isArray(income)  ? income[3]  : {};
  const cf = Array.isArray(cash)    ? cash[0]    : {};
  const bs = Array.isArray(balance) ? balance[0] : {};
  if (!q.price) throw new Error("FMP returned no data for " + t);

  let revenueGrowthRate = null;
  if (i0.revenue && i3?.revenue && i3.revenue !== 0)
    revenueGrowthRate = Math.pow(i0.revenue / i3.revenue, 1/3) - 1;
  else if (i0.revenue && i1?.revenue && i1.revenue !== 0)
    revenueGrowthRate = (i0.revenue - i1.revenue) / Math.abs(i1.revenue);

  const beta = q.beta || 1.0;
  const rf = 0.043;
  const erp = 0.055;
  const ke = rf + beta * erp;
  const mc = q.marketCap || 0;
  const td = bs.totalDebt || 0;
  const wacc = (mc + td) > 0 ? (ke * mc + 0.04 * 0.79 * td) / (mc + td) : ke;

  // ── Derived calculations ────────────────────────────────────────────────────
  // Shares outstanding — from income statement since quote doesn't have it
  const sharesOut = i0.weightedAverageShsOut || i0.weightedAverageShsOutDil ||
                    cf.weightedAverageShsOut || null;
  const fcf       = cf.freeCashFlow || null;
  const ebitda    = i0.ebitda || null;
  const netDebtVal = bs.netDebt != null ? bs.netDebt :
                    (bs.totalDebt ? bs.totalDebt - (bs.cashAndShortTermInvestments||bs.cashAndCashEquivalents||0) : null);

  // EPS — from income statement (use != null so legitimate zero EPS isn't skipped)
  const eps = i0.eps != null ? i0.eps
            : i0.epsdiluted != null ? i0.epsdiluted
            : (i0.netIncome != null && sharesOut ? i0.netIncome / sharesOut : null);

  // P/E ratio — price / eps
  const peRatio = eps && q.price && eps > 0 ? q.price / eps : null;

  // Book value per share
  const equity = bs.totalStockholdersEquity || bs.totalEquity || null;
  const bvps   = sharesOut && equity ? equity / sharesOut : null;

  // Revenue per share
  const revps = sharesOut && i0.revenue ? i0.revenue / sharesOut : null;

  // Dividend per share from cash flow
  const cf1    = Array.isArray(cash) ? cash[1] : {};
  const divRaw = cf.commonDividendsPaid || cf.dividendsPaid || null;
  const divps  = divRaw && sharesOut ? Math.abs(divRaw) / sharesOut : null;

  // Trailing 1-year dividend growth rate (year-over-year from cash flow history)
  const divRaw1 = cf1.commonDividendsPaid || cf1.dividendsPaid || null;
  const divps1  = divRaw1 && sharesOut ? Math.abs(divRaw1) / sharesOut : null;
  const dividendGrowthRate = (divps && divps1 && divps1 > 0.001)
    ? Math.max(-0.20, Math.min(0.15, (divps / divps1) - 1))
    : 0.03;

  // Payout ratio
  const payoutR = eps && divps && eps > 0 ? divps / eps : null;

  return {
    ticker: t,
    companyName: q.name || t,
    currentPrice: q.price || null,
    dataDate: new Date().toLocaleDateString("en-US", { month:"long", year:"numeric" }),
    sector: null,
    industry: null,
    // Income data
    eps: eps,
    revenue: i0.revenue || null,
    netIncome: i0.netIncome || cf.netIncome || null,
    ebitda,
    revenueGrowthRate,
    // Balance sheet
    bookValuePerShare: bvps,
    revenuePerShare: revps,
    sharesOutstanding: sharesOut,
    netDebt: netDebtVal,
    totalDebt: bs.totalDebt || null,
    enterpriseValue: m.enterpriseValueTTM || null,
    // Cash flow
    freeCashFlow: fcf,
    operatingCashFlow: cf.operatingCashFlow || null,
    // Dividends
    dividendPerShare: divps,
    payoutRatio: payoutR,
    dividendGrowthRate,
    // Risk
    beta,
    riskFreeRate: rf,
    equityRiskPremium: erp,
    wacc,
    // Ratios
    peRatioTTM: peRatio,
    evToEbitdaTTM: m.evToEBITDATTM || null,
    pbRatioTTM: m.pbRatioTTM || null,
    marketCap: mc || null,
    peers: [],
    // Analyst consensus (Street)
    analystTarget:     parseFloat(ptData.targetConsensus || ptData.priceTarget || 0) || null,
    analystTargetHigh: parseFloat(ptData.targetHigh || 0) || null,
    analystTargetLow:  parseFloat(ptData.targetLow  || 0) || null,
  };
}

export default fetchFMP;
